import { useMemo, useState, type FormEvent } from 'react';
import { Link } from '@tanstack/react-router';
import { Button } from '@nexply/ui';
import { api } from '../lib/api.js';
import { FloatingField } from '../components/FloatingField.js';
import { BrandSide } from './login.js';
import { LegalFooter } from '../components/LegalFooter.js';

export function ResetPasswordPage() {
  // Der Token kommt als ?token=… aus der E-Mail — bewusst direkt aus der URL
  // gelesen, damit die Route kein eigenes Search-Schema braucht.
  const token = useMemo(() => new URLSearchParams(window.location.search).get('token') ?? '', []);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }
    setBusy(true);
    try {
      await api.auth.resetPassword({ token, password });
      setDone(true);
    } catch (err) {
      const e = err as { code?: string; status?: number; message?: string };
      setError(
        e.code === 'invalid_token'
          ? 'Dieser Link ist abgelaufen oder wurde schon benutzt. Fordere unten einen neuen an.'
          : e.status === 429
            ? 'Zu viele Versuche — bitte warte ein paar Minuten.'
            : e.message ?? 'Zurücksetzen fehlgeschlagen.',
      );
    } finally {
      setBusy(false);
    }
  }

  const invalidLink = token.length < 20;

  return (
    <div className="relative min-h-dvh w-full overflow-hidden bg-ink lg:grid lg:grid-cols-[1.2fr_1fr] xl:grid-cols-[1.4fr_1fr]">
      <BrandSide />

      <div className="relative z-10 flex min-h-dvh w-full flex-col">
        <div className="lg:hidden pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-[420px] w-[420px] rounded-full bg-gold/15 blur-[120px]" />
        <div className="lg:hidden pointer-events-none absolute bottom-0 right-0 h-[280px] w-[280px] rounded-full bg-gold/10 blur-[100px]" />

        <div className="relative z-10 mx-auto flex w-full max-w-sm flex-1 flex-col px-6 pt-[calc(env(safe-area-inset-top)+48px)] pb-[calc(env(safe-area-inset-bottom)+24px)] lg:max-w-md lg:justify-center lg:py-12 lg:px-10">
          <div className="text-center mb-10 lg:hidden animate-slide-up">
            <h1 className="text-6xl font-semibold tracking-tighter text-gold-grad">nexply</h1>
            <p className="mt-3 text-[11px] uppercase tracking-[0.4em] text-gold/70">Life is a Puzzle</p>
          </div>

          {done ? (
            <div className="flex flex-1 flex-col gap-4 animate-slide-up lg:flex-none lg:rounded-3xl lg:border lg:border-line lg:bg-surface-1 lg:p-8 lg:shadow-card-lg">
              <h2 className="text-2xl font-semibold tracking-tight text-text">Passwort geändert</h2>
              <p className="text-sm text-text-dim">
                Dein neues Passwort ist gespeichert. Aus Sicherheitsgründen wurdest du überall
                abgemeldet — melde dich jetzt neu an.
              </p>
              <Link to="/login">
                <Button size="lg" fullWidth>Zur Anmeldung</Button>
              </Link>
            </div>
          ) : invalidLink ? (
            <div className="flex flex-1 flex-col gap-4 animate-slide-up lg:flex-none lg:rounded-3xl lg:border lg:border-line lg:bg-surface-1 lg:p-8 lg:shadow-card-lg">
              <h2 className="text-2xl font-semibold tracking-tight text-text">Link unvollständig</h2>
              <p className="text-sm text-text-dim">
                Dieser Seite fehlt der Reset-Code. Öffne den Link aus der E-Mail direkt — oder fordere
                einen neuen an.
              </p>
              <Link to="/forgot-password">
                <Button size="lg" fullWidth variant="secondary">Neuen Link anfordern</Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="flex flex-1 flex-col gap-4 animate-slide-up lg:flex-none lg:rounded-3xl lg:border lg:border-line lg:bg-surface-1 lg:p-8 lg:shadow-card-lg">
              <div>
                <h2 className="text-2xl font-semibold tracking-tight text-text">Neues Passwort</h2>
                <p className="mt-1 text-sm text-text-dim">Wähle ein neues Passwort für dein Konto.</p>
              </div>

              <div className="space-y-3 mt-2">
                <FloatingField
                  id="password"
                  label="Neues Passwort"
                  type="password"
                  autoComplete="new-password"
                  minLength={8}
                  value={password}
                  onChange={setPassword}
                  required
                  hint="Mindestens 8 Zeichen."
                />
                <FloatingField
                  id="confirm"
                  label="Passwort wiederholen"
                  type="password"
                  autoComplete="new-password"
                  minLength={8}
                  value={confirm}
                  onChange={setConfirm}
                  required
                />
              </div>

              {error && (
                <div className="text-xs text-danger bg-danger/10 border border-danger/25 rounded-xl px-4 py-3">
                  {error}{' '}
                  {error.includes('abgelaufen') && (
                    <Link to="/forgot-password" className="font-medium text-gold-light hover:text-gold transition">
                      Neuen Link anfordern
                    </Link>
                  )}
                </div>
              )}

              <Button type="submit" size="lg" fullWidth loading={busy}>
                Passwort speichern
              </Button>

              <div className="mt-auto lg:mt-2 pt-8 lg:pt-4 text-center text-sm text-text-dim">
                <Link to="/login" className="font-medium text-gold-light hover:text-gold transition">
                  Zurück zur Anmeldung
                </Link>
              </div>
            </form>
          )}
          <LegalFooter className="mt-8 lg:mt-6" />
        </div>
      </div>
    </div>
  );
}
